import { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  useIsMobile,
} from '@design-system';
import { ChevronLeft } from 'lucide-react';

const DEMO_VIDEO_URL = 'https://www.loom.com/embed/49a4f641f8a44840be0a678bfbf9351e';
const CALENDLY_BOOK_DEMO_URL =
  'https://calendly.com/d/csmf-cbc-z7d/waterlily-demo?_gl=1%2A2j6i5g%2A_ga%2AMTE3NTIwODgwMC4xNzY4MjEzMDQ0%2A_ga_DRTVFLQGM7%2AczE3NzE1OTUxMDAkbzE0JGcxJHQxNzcxNTk1MTE0JGo0NiRsMCRoMA..&month=2026-02';

function buildVideoSrc(baseUrl: string): string {
  const url = new URL(baseUrl);
  url.searchParams.set('autoplay', '0');
  url.searchParams.set('mute', '0');
  url.searchParams.set('t', '0');
  return url.toString();
}

interface BookDemoVideoModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onBack?: () => void;
  videoUrl?: string;
}

const VIDEO_HEADING = "We're confident you'll be amazed";
const VIDEO_DESCRIPTION =
  "Here's a quick demo showcasing how it helps advisors and agents like you. Remember, our 14 day trial is commitment and risk free";
const VIDEO_FOOTER_TEXT =
  "If you still have questions after the demo, you can book a free call with our team. We'd love to help.";

export function BookDemoVideoModal({
  open,
  onOpenChange,
  onBack,
  videoUrl = DEMO_VIDEO_URL,
}: BookDemoVideoModalProps) {
  const isMobile = useIsMobile();
  const [iframeKey, setIframeKey] = useState(Date.now().toString());

  useEffect(() => {
    if (open) {
      setIframeKey(Date.now().toString());
    }
  }, [open]);

  const videoSrc = buildVideoSrc(videoUrl);

  const handleVideoEnough = () => {
    onOpenChange(false);
  };

  const handleStillWantCall = () => {
    window.open(CALENDLY_BOOK_DEMO_URL, '_blank');
    onOpenChange(false);
  };

  const backButton = onBack ? (
    <Button
      type="button"
      variant="outline"
      size="icon-sm"
      onClick={onBack}
      aria-label="Go back"
      className="absolute left-3 top-4 rounded-full"
    >
      <ChevronLeft className="h-4 w-4" />
    </Button>
  ) : null;

  const videoBlock = (
    <div className="px-6 py-4">
      <div
        className="relative w-full overflow-hidden rounded-xl bg-black"
        style={{ paddingBottom: '56.25%' }}
      >
        {open && (
          <iframe
            key={iframeKey}
            src={videoSrc}
            title="Waterlily demo video"
            frameBorder="0"
            allowFullScreen
            allow="autoplay; fullscreen"
            className="absolute left-0 top-0 h-full w-full"
          />
        )}
      </div>
    </div>
  );

  const footerButtons = (
    <>
      <Button
        onClick={handleVideoEnough}
        size="lg"
        className="flex-1"
      >
        Video was enough
      </Button>
      <Button
        onClick={handleStillWantCall}
        variant="secondary"
        size="lg"
        className="flex-1"
      >
        I'd like a call
      </Button>
    </>
  );

  if (isMobile) {
    return (
      <Drawer
        open={open}
        onOpenChange={onOpenChange}
      >
        <DrawerContent className="flex max-h-[90vh] flex-col">
          <div className="relative px-6 pb-2 text-center">
            {backButton}
            <DrawerHeader className="text-center">
              <DrawerTitle className="text-foreground text-center text-xl font-bold">
                {VIDEO_HEADING}
              </DrawerTitle>
              <DrawerDescription className="text-foreground mx-auto mt-1 max-w-sm text-center text-sm">
                {VIDEO_DESCRIPTION}
              </DrawerDescription>
            </DrawerHeader>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-0">
            {videoBlock}
            <p className="text-foreground px-6 text-center text-sm">{VIDEO_FOOTER_TEXT}</p>
          </div>
          <DrawerFooter className="flex-row gap-3 px-6 pb-8 pt-4">{footerButtons}</DrawerFooter>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
    >
      <DialogContent className="flex max-h-[90vh] flex-col gap-0 overflow-y-auto p-0">
        <div className="relative px-8 pb-2 pt-8 text-center">
          {backButton}
          <DialogHeader className="text-center">
            <DialogTitle className="text-foreground mt-4 text-center text-2xl font-bold">
              {VIDEO_HEADING}
            </DialogTitle>
            <DialogDescription className="text-foreground mx-auto mt-1 max-w-sm text-center text-sm">
              {VIDEO_DESCRIPTION}
            </DialogDescription>
          </DialogHeader>
        </div>
        {videoBlock}
        <p className="text-foreground px-8 text-center text-sm">{VIDEO_FOOTER_TEXT}</p>
        <DialogFooter className="flex-row gap-3 px-8 pb-8 pt-4">{footerButtons}</DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
